import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { PageTransition } from '@/components/PageTransition';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Clock,
  Calendar,
  GitBranch,
  Trash2,
  Save,
  X,
  Play,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/LoadingSpinner';

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sun', fullLabel: 'Sunday' },
  { value: 1, label: 'Mon', fullLabel: 'Monday' },
  { value: 2, label: 'Tue', fullLabel: 'Tuesday' },
  { value: 3, label: 'Wed', fullLabel: 'Wednesday' },
  { value: 4, label: 'Thu', fullLabel: 'Thursday' },
  { value: 5, label: 'Fri', fullLabel: 'Friday' },
  { value: 6, label: 'Sat', fullLabel: 'Saturday' },
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export default function ScheduleEditorPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const scheduleId = searchParams.get('id');
  const { userProfile, company } = useAuth();
  const { space, spaceTools, loading: spaceLoading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  const isEditing = !!scheduleId;
  usePageTitle(isEditing ? 'Edit Schedule' : 'New Schedule');

  // Redirect if no access
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  // Form state
  const [name, setName] = useState('');
  const [workflowId, setWorkflowId] = useState('');
  const [frequency, setFrequency] = useState('weekly');
  const [time, setTime] = useState('09:00');
  const [timezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [selectedDays, setSelectedDays] = useState([1, 2, 3, 4, 5]); // Mon-Fri
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [monthOfYear, setMonthOfYear] = useState(1);
  const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Data state
  const [workflows, setWorkflows] = useState([]);
  const [loadingWorkflows, setLoadingWorkflows] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEditing);

  // Navigation helper
  const goBack = () => navigate(`/space/${projectId}/tasks?view=schedules`);

  // Fetch workflows
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const { data, error } = await supabase
          .from('workflows')
          .select('id, name, description, status')
          .eq('project_id', projectId)
          .eq('status', 'published')
          .order('name');

        if (error) throw error;
        setWorkflows(data || []);
      } catch (error) {
        console.error('Error fetching workflows:', error);
        toast.error('Failed to load workflows');
      } finally {
        setLoadingWorkflows(false);
      }
    };

    if (projectId) fetchWorkflows();
  }, [projectId]);

  // Fetch existing schedule if editing
  useEffect(() => {
    if (!scheduleId) return;

    const fetchSchedule = async () => {
      try {
        const { data, error } = await supabase
          .from('workflow_schedules')
          .select('*')
          .eq('id', scheduleId)
          .single();

        if (error) throw error;

        if (data) {
          setName(data.name || '');
          setWorkflowId(data.workflow_id || '');
          setFrequency(data.frequency || 'weekly');
          setTime(data.time || '09:00');
          setSelectedDays(data.days_of_week || [1, 2, 3, 4, 5]);
          setDayOfMonth(data.day_of_month || 1);
          setMonthOfYear(data.month_of_year || 1);
          setStartDate(data.start_date || new Date().toISOString().split('T')[0]);
          setEndDate(data.end_date || '');
          setIsActive(data.is_active !== false);
        }
      } catch (error) {
        console.error('Error fetching schedule:', error);
        toast.error('Failed to load schedule');
        goBack();
      } finally {
        setLoading(false);
      }
    };

    fetchSchedule();
  }, [scheduleId]);

  // Calculate next run date
  const nextRunDate = useMemo(() => {
    const now = new Date();
    const [hours, minutes] = time.split(':').map(Number);
    let nextRun = new Date();
    nextRun.setHours(hours, minutes, 0, 0);

    if (nextRun <= now) {
      nextRun.setDate(nextRun.getDate() + 1);
    }

    switch (frequency) {
      case 'once':
        nextRun = new Date(startDate);
        nextRun.setHours(hours, minutes, 0, 0);
        break;
      case 'weekly':
        while (!selectedDays.includes(nextRun.getDay())) {
          nextRun.setDate(nextRun.getDate() + 1);
        }
        break;
      case 'monthly':
        nextRun.setDate(dayOfMonth);
        if (nextRun <= now) {
          nextRun.setMonth(nextRun.getMonth() + 1);
        }
        break;
      case 'yearly':
        nextRun.setMonth(monthOfYear - 1);
        nextRun.setDate(dayOfMonth);
        if (nextRun <= now) {
          nextRun.setFullYear(nextRun.getFullYear() + 1);
        }
        break;
    }

    return nextRun;
  }, [time, frequency, startDate, selectedDays, dayOfMonth, monthOfYear]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error('Please enter a schedule name');
      return;
    }
    if (!workflowId) {
      toast.error('Please select a workflow');
      return;
    }
    if (frequency === 'weekly' && selectedDays.length === 0) {
      toast.error('Please select at least one day');
      return;
    }

    setSaving(true);
    try {
      const scheduleData = {
        name: name.trim(),
        workflow_id: workflowId,
        project_id: projectId,
        created_by: userProfile?.id,
        frequency,
        time,
        timezone,
        days_of_week: frequency === 'weekly' ? selectedDays : null,
        day_of_month: ['monthly', 'yearly'].includes(frequency) ? dayOfMonth : null,
        month_of_year: frequency === 'yearly' ? monthOfYear : null,
        start_date: startDate,
        end_date: endDate || null,
        is_active: isActive,
        next_run_at: nextRunDate.toISOString(),
      };

      if (scheduleId) {
        const { error } = await supabase
          .from('workflow_schedules')
          .update(scheduleData)
          .eq('id', scheduleId);
        if (error) throw error;
        toast.success('Schedule updated');
      } else {
        const { error } = await supabase
          .from('workflow_schedules')
          .insert(scheduleData);
        if (error) throw error;
        toast.success('Schedule created');
      }

      goBack();
    } catch (error) {
      console.error('Error saving schedule:', error);
      toast.error('Failed to save schedule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!scheduleId) return;
    if (!confirm('Are you sure you want to delete this schedule?')) return;

    try {
      const { error } = await supabase
        .from('workflow_schedules')
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;
      toast.success('Schedule deleted');
      goBack();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      toast.error('Failed to delete schedule');
    }
  };

  const toggleDay = (day) => {
    setSelectedDays(prev =>
      prev.includes(day)
        ? prev.filter(d => d !== day)
        : [...prev, day].sort((a, b) => a - b)
    );
  };

  const selectedWorkflow = workflows.find(w => w.id === workflowId);

  // Breadcrumbs for navigation
  const breadcrumbs = useMemo(() => [{
    name: isEditing ? 'Edit Schedule' : 'New Schedule',
    onToolClick: goBack
  }], [isEditing, projectId]);

  if (loading || spaceLoading) {
    return (
      <AppShell3>
        <div className="flex items-center justify-center h-96">
          <LoadingSpinner />
        </div>
      </AppShell3>
    );
  }

  return (
    <AppShell3>
      <PageTransition>
        <ToolHeader
          spaceName={space?.name || 'Space'}
          spaceImage={space?.logo_url}
          toolName="Schedules"
          currentTool="tasks"
          spaceTools={spaceTools || []}
          breadcrumbs={breadcrumbs}
          actions={
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Label htmlFor="active-toggle" className="text-sm text-muted-foreground">
                  {isActive ? 'Active' : 'Paused'}
                </Label>
                <Switch
                  id="active-toggle"
                  checked={isActive}
                  onCheckedChange={setIsActive}
                />
              </div>
              <div className="w-px h-6 bg-border" />
              <Button variant="outline" size="sm" onClick={goBack}>
                Cancel
              </Button>
              <Button size="sm" onClick={handleSave} disabled={saving}>
                {saving ? (
                  <LoadingSpinner className="h-4 w-4 mr-1.5" />
                ) : (
                  <Save className="h-4 w-4 mr-1.5" />
                )}
                {isEditing ? 'Save' : 'Create'}
              </Button>
            </div>
          }
        />

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Basic Details Card */}
            <Card>
              <CardHeader>
                <CardTitle>Schedule Details</CardTitle>
                <CardDescription>
                  Give your schedule a name and select which workflow to run
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    placeholder="e.g., Weekly Safety Check"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Workflow</Label>
                  <Select value={workflowId} onValueChange={setWorkflowId}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a workflow..." />
                    </SelectTrigger>
                    <SelectContent>
                      {loadingWorkflows ? (
                        <div className="p-4 text-center text-muted-foreground">
                          <LoadingSpinner className="h-4 w-4 mx-auto mb-2" />
                          Loading...
                        </div>
                      ) : workflows.length === 0 ? (
                        <div className="p-4 text-center">
                          <AlertCircle className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                          <p className="text-sm text-muted-foreground">No published workflows</p>
                        </div>
                      ) : (
                        workflows.map((workflow) => (
                          <SelectItem key={workflow.id} value={workflow.id}>
                            <div className="flex items-center gap-2">
                              <GitBranch className="h-4 w-4 text-blue-600" />
                              <span>{workflow.name}</span>
                            </div>
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  {selectedWorkflow?.description && (
                    <p className="text-sm text-muted-foreground">
                      {selectedWorkflow.description}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Frequency Card */}
            <Card>
              <CardHeader>
                <CardTitle>Frequency</CardTitle>
                <CardDescription>
                  How often should this workflow run?
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <RadioGroup value={frequency} onValueChange={setFrequency}>
                  <div className="grid grid-cols-5 gap-4">
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="once" id="once" />
                      <Label htmlFor="once" className="cursor-pointer">Once</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="daily" id="daily" />
                      <Label htmlFor="daily" className="cursor-pointer">Daily</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="weekly" id="weekly" />
                      <Label htmlFor="weekly" className="cursor-pointer">Weekly</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="monthly" id="monthly" />
                      <Label htmlFor="monthly" className="cursor-pointer">Monthly</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yearly" id="yearly" />
                      <Label htmlFor="yearly" className="cursor-pointer">Yearly</Label>
                    </div>
                  </div>
                </RadioGroup>

                {/* Time */}
                <div className="space-y-2">
                  <Label htmlFor="time">Time</Label>
                  <div className="flex items-center gap-3">
                    <Input
                      id="time"
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className="w-32"
                    />
                    <span className="text-sm text-muted-foreground">{timezone}</span>
                  </div>
                </div>

                {/* Weekly - Day Selection */}
                {frequency === 'weekly' && (
                  <div className="space-y-3">
                    <Label>Days of the week</Label>
                    <div className="flex flex-wrap gap-4">
                      {DAYS_OF_WEEK.map((day) => (
                        <div key={day.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`day-${day.value}`}
                            checked={selectedDays.includes(day.value)}
                            onCheckedChange={() => toggleDay(day.value)}
                          />
                          <Label
                            htmlFor={`day-${day.value}`}
                            className="cursor-pointer text-sm"
                          >
                            {day.fullLabel}
                          </Label>
                        </div>
                      ))}
                    </div>
                    {selectedDays.length === 0 && (
                      <p className="text-sm text-destructive">Select at least one day</p>
                    )}
                  </div>
                )}

                {/* Monthly - Day of Month */}
                {frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label>Day of the month</Label>
                    <Select value={String(dayOfMonth)} onValueChange={(v) => setDayOfMonth(Number(v))}>
                      <SelectTrigger className="w-24">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                          <SelectItem key={day} value={String(day)}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Yearly - Month and Day */}
                {frequency === 'yearly' && (
                  <div className="flex gap-4">
                    <div className="space-y-2">
                      <Label>Month</Label>
                      <Select value={String(monthOfYear)} onValueChange={(v) => setMonthOfYear(Number(v))}>
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MONTHS.map((month) => (
                            <SelectItem key={month.value} value={String(month.value)}>
                              {month.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Day</Label>
                      <Select value={String(dayOfMonth)} onValueChange={(v) => setDayOfMonth(Number(v))}>
                        <SelectTrigger className="w-24">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map((day) => (
                            <SelectItem key={day} value={String(day)}>
                              {day}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}

                {/* Once - Date Picker */}
                {frequency === 'once' && (
                  <div className="space-y-2">
                    <Label htmlFor="start-date">Date</Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-44"
                    />
                  </div>
                )}

                {/* End Date for Recurring */}
                {frequency !== 'once' && (
                  <div className="space-y-2">
                    <Label htmlFor="end-date">
                      End date <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-44"
                    />
                    <p className="text-sm text-muted-foreground">
                      Leave empty to run indefinitely
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Preview Card */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Play className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Next scheduled run</p>
                    <p className="font-medium">
                      {nextRunDate.toLocaleDateString('en-GB', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                      {' at '}
                      {nextRunDate.toLocaleTimeString('en-GB', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Delete */}
            {isEditing && (
              <Card className="border-destructive/50">
                <CardHeader>
                  <CardTitle className="text-destructive">Danger Zone</CardTitle>
                  <CardDescription>
                    Permanently delete this schedule
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <Button variant="destructive" onClick={handleDelete}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Schedule
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </PageTransition>
    </AppShell3>
  );
}
